import * as legacyReleaseNotesModule from "../../../web/lib/releaseNotes";
import type {
  ReleaseAudience,
  ReleaseConfidence,
  ReleaseEntry,
} from "../../../web/lib/releaseNotes";
import { releaseNotesV202ToV461 } from "./releaseNotesV202ToV461";
import { releaseNotesBackfill20260731To20260923 } from "./releaseNotesBackfill20260731To20260923";

// The legacy changelog lives outside the Cloudflare package boundary. Vite
// exposes its named exports directly, while Node's TypeScript test loader can
// expose the same module through a CommonJS-compatible default export. Keep
// both runtimes on the same source without duplicating release history.
const legacyReleaseNotesExport = legacyReleaseNotesModule as typeof legacyReleaseNotesModule & {
  default?: typeof legacyReleaseNotesModule;
};
const legacyReleaseNotes =
  legacyReleaseNotesExport.releaseNotes ?? legacyReleaseNotesExport.default?.releaseNotes ?? [];

const recentReleaseNoteSources: ReleaseEntry[] = [
  {
    version: 490,
    date: "2026-09-23",
    title: "KenPost Logo Alanı Sadeleştirildi",
    summary:
      "Müşteri panelindeki KenPost logosunun çift zemin ve çerçeve görünümü kaldırıldı; logo daha geniş, şeffaf ve dengeli bir yerleşime geçirildi.",
    confidence: "verified",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Logo görselindeki opak açık gri zemin şeffaflaştırıldı ve bileşenin eklediği yuvarlatılmış kart ile çerçeve kaldırıldı.",
      "Masaüstü ve mobil kenar çubuğunda logo boyutu artırılarak boş alan daha dengeli kullanıldı.",
      "Kısa ekranlarda uygulanan sıkıştırılmış menü düzeninde logo okunaklı kalacak şekilde yeniden ölçülendirildi.",
    ],
    details: [{
      title: "Temiz Marka Alanı",
      items: [
        "Logo artık kenar çubuğunun doğal beyaz zemini üzerinde ayrı bir kutu görünümü oluşturmadan gösterilir.",
        "Orijinal sembol, yazı, renkler ve slogan korunarak yalnız arka plan katmanı kaldırıldı.",
      ],
    }],
  },
  {
    version: 489,
    date: "2026-09-23",
    title: "API Kötüye Kullanım Koruması ve Kullanıcı Menüsü",
    summary:
      "Müşteri API'sine anahtar ve hesap düzeyinde katmanlı istek sınırları eklendi; profil, ekip ve destek bağlantıları sol menüden kullanıcı açılır menüsüne taşındı.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "API anahtarına özel dakikalık sınırın yanında, birden çok anahtarla aşılamayan hesap-geneli dakika sınırı eklendi.",
      "Etiket oluşturma istekleri anahtar ve hesap düzeyinde dakika/gün kotalarıyla korundu; günlük anahtar kotası oluşturma sırasında ayarlanabilir.",
      "Limit aşımında 429 yanıtı ve doğru Retry-After süresi döner; idempotent etiket tekrarları çift kredi tüketmeden çalışmaya devam eder.",
      "Profil, Ekip yönetimi ve Bize ulaşın bağlantıları masaüstü ve mobilde sağ üst kullanıcı menüsünden açılır.",
    ],
    details: [{
      title: "Katmanlı API Güvenliği",
      items: [
        "Sızan tek bir anahtarın yüksek hacimli istek veya etiket üretimiyle hesabı ve ortak altyapıyı zorlaması, bağımsız anahtar ve hesap sayaçlarıyla sınırlandırıldı.",
        "OpenAPI dokümanı ve müşteri ekranı 429, Retry-After ve günlük etiket kotası davranışlarını açıkça gösterir.",
      ],
    }],
  },
  {
    version: 488,
    date: "2026-09-16",
    title: "Faturalarda Türkçe Karakter Desteği",
    summary:
      "Firma unvanı, isim veya adresindeki Türkçe karakterler nedeniyle indirilemeyen faturalar düzeltildi; müşteri bilgileri değiştirilmeden PDF içinde aynen korunur.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Sipariş ve etiket satın alma faturalarında normal ve kalın yazı tipleri Türkçe karakter destekleyen gömülü fontlara geçirildi.",
      "İ, ı, Ş, ş, Ğ ve ğ gibi karakterlerin WinAnsi hatası oluşturması giderildi; Almanca karakterler ve mevcut fatura düzeni korundu.",
      "Yazı tipleri sunucu paketine dahil edildi; fatura oluşturulurken dışarıdan font indirme ihtiyacı kaldırıldı.",
      "Ödeme tutarları, ödeme durumları ve müşteri kayıtları değiştirilmedi; mevcut faturalar tekrar indirilebilir.",
    ],
    details: [{
      title: "Unicode Fatura Kontrolü",
      items: [
        "İsimli Tesbih firma unvanındaki İ harfiyle oluşan hata aynı fatura oluşturucusunda yeniden üretildi ve giderildi.",
        "Türkçe ve Almanca isim, firma, adres ve kalın yazı alanları için PDF oluşturma regresyon kontrolü eklendi.",
      ],
    }],
  },
  {
    version: 487,
    date: "2026-09-15",
    title: "WooCommerce Ödeme Bekleyen Sipariş Güvenliği",
    summary:
      "WooCommerce'ta ödemesi bekleyen pending ve on-hold siparişler artık KenPost'un gönderime hazır sipariş kuyruğuna alınmaz; ödeme tamamlanınca sonraki senkronla otomatik olarak açılır.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "WooCommerce pending, on-hold ve checkout-draft durumları etiket oluşturulabilir açık siparişlerden ayrıldı.",
      "Ödeme bekleyen kayıtlar denetim amacıyla korunur, fakat etiket seçimine ve gönderi oluşturmaya kapalı kalır.",
      "WooCommerce durumu processing olduğunda sipariş sonraki senkronla kendiliğinden gönderime hazır hale gelir.",
      "İptal, iade, başarısız ve tamamlanmış siparişlerin mevcut durum eşlemeleri korunur.",
    ],
    details: [
      {
        title: "Anadolu Tadı WooCommerce Kontrolü",
        items: [
          "TurkMarket.eu mağazasındaki LK117134 siparişinin WooCommerce'ta on-hold olmasına rağmen KenPost'ta açık görünmesinin durum eşlemesinden kaynaklandığı doğrulandı.",
          "Durum güvenliği yalnız liste görünümünde değil, siparişin iç gönderim uygunluğunda uygulanarak yanlışlıkla etiket oluşturma riski kaldırıldı.",
        ],
      },
    ],
  },
  {
    version: 486,
    date: "2026-09-13",
    title: "100 Üzeri Siparişte Toplu Etiket Kontrolü Düzeltildi",
    summary: "100'den fazla sipariş seçildiğinde açıklamasız kapanan toplu etiket kontrolü düğmesi düzeltildi; 250 siparişe kadar seçim, küçük oluşturma grupları ve güvenli PDF parçalarıyla işlenir.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "102 sipariş gibi 100 üzerindeki seçimlerde toplu kontrol penceresi açılır; mevcut 250 siparişlik seçim sınırı korunur.",
      "Etiket oluşturma istekleri 10 siparişlik gruplarla devam eder; tek PDF'nin 100 etiketlik bellek sınırı değiştirilmez.",
      "Büyük işlemler numaralı PDF dosyalarına ayrılır ve her PDF toplu geçmişten tekrar indirilebilir.",
      "Tamamlanan parçaların sonuçları ve yeniden indirme kimlikleri işlem sırasında korunur; uygun olmayan siparişlerde açık açıklama gösterilir.",
      "Yeni Git kaydı olmadan yapılan destek dağıtımlarında da benzersiz uygulama sürümü üretilir; eski tarayıcı dosyalarının tekrar yüklenmesi önlenir.",
    ],
    details: [{ title: "Kaffee Intakt Toplu Seçim Kontrolü", items: [
      "Müşterinin videosundaki 102 siparişlik seçim ile kodun 100 siparişlik sessiz düğme engeli karşılaştırılarak sorun belirlendi.",
      "Almanca ve İngilizce sınır, hazırlık ve çok parçalı PDF açıklamaları güncellendi.",
    ] }],
  },
  {
    version: 485,
    date: "2026-09-12",
    title: "DHL Uzun Alıcı ve Şehir Alanları Otomatik Sığdırılıyor",
    summary:
      "DHL'nin 35 karakter sınırını aşan alıcı adı ve posta kodu-şehir satırı, kaynak sipariş verisi değiştirilmeden etiket çıktısında baştaki bölüm korunarak güvenli uzunluğa otomatik sığdırılır.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Uzun alıcı adlarında baştaki anlamlı bölüm korunur ve yalnızca DHL'e gönderilen etiket alanı kısaltılır.",
      "Şehir için kullanılabilir uzunluk posta koduna göre hesaplanır; posta kodu ve şehir satırı birlikte 35 karakteri geçmez.",
      "Sokak, kapı numarası ve adres eki gibi teslimatı riske atabilecek alanlar otomatik kesilmez ve gerektiğinde kullanıcıyı uyarmaya devam eder.",
    ],
    details: [
      {
        title: "PlentyONE Son Engel Kontrolü",
        items: [
          "Daha önce yalnızca uzun alıcı adı veya şehir satırı nedeniyle bekleyen siparişler etiket oluşturmaya uygun hale getirildi.",
          "Mağazadan alınan ham ad ve adres bilgileri eksiksiz korunur; düzeltme yalnızca DHL etiket isteği hazırlanırken uygulanır.",
          "Canlı müşteri hesabında etiket veya gönderi oluşturmadan seçim ve ön kontrol akışı yeniden doğrulandı.",
        ],
      },
    ],
  },
  {
    version: 484,
    date: "2026-09-12",
    title: "PlentyONE Sipariş Ayrıntıları Güçlendirildi",
    summary:
      "PlentyONE siparişlerinin ürün, tutar, ödeme ve gerçek satış kanalı bilgileri liste ve ayrıntı ekranlarında eksiksiz gösterilir; uzun geçmiş aktarımları da eksik sonuçla başarılı sayılmaz.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Sipariş satırında PlentyONE ile birlikte Amazon, eBay, Shopify, PlentyPOS veya manuel kaynak kanal görünür.",
      "Ürün adı, adet, SKU, sipariş toplamı, para birimi ve ödeme durumu PlentyONE verisinden okunur; kargo ücreti ürün adedine dahil edilmez.",
      "Sipariş ayrıntı ekranında kaynak mağaza ve kaynak kanal ayrıca gösterilir.",
      "Çok sayfalı geçmiş aktarımı sayfa sınırına ulaşırsa eksik veriyi başarılı saymak yerine açık hata verir; aktarılan ham veri güvenli biçimde küçültülür ve müşteri ekranındaki 3 ay seçimi PlentyONE'a aynen uygulanır.",
    ],
    details: [
      {
        title: "Müşteri Hesabı Son Kontrolü",
        items: [
          "customer@kenpost.de PlentyONE mağazasında 410 siparişin kimlik benzersizliği, kaynak bağlantıları, durumları ve adres kullanılabilirliği yeniden doğrulandı.",
          "Açık siparişlerin tamamında kullanılabilir alıcı ve kargo adresi bulundu; adresi olmayan beş kayıt iptal edilmiş Amazon siparişleridir.",
          "Takip geri-yazma isteğinin adresi, kimliği ve gövdesi canlı sisteme veri göndermeyen kontrollü testle doğrulandı.",
        ],
      },
    ],
  },
  {
    version: 483,
    date: "2026-09-12",
    title: "PlentyONE Kaynak Kanalı ve Mağaza Kontrolleri",
    summary:
      "PlentyONE üzerinden alınan siparişlerde asıl satış kanalı görünür hale getirildi; sipariş dönemi ve takip geri-yazma davranışı mağaza bazında yönetilebilir.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "PlentyONE siparişleri Amazon, eBay, Shopify, manuel giriş ve PlentyPOS gibi gerçek kaynak kanallarıyla gösterilir.",
      "Kaynak kanal seçimiyle PlentyONE sipariş listesi daraltılabilir; pazar yeri sipariş numarası listede korunur.",
      "Her PlentyONE mağazasında takip ve gönderim bilgisinin geri yazılıp yazılmayacağı ayrı bir anahtarla seçilir.",
      "Otomatik senkron için son 1 gün, 3 gün, 7 gün, 30 gün, 3 ay veya tüm geçmiş seçeneklerinden biri mağaza varsayılanı olarak saklanır.",
    ],
    details: [
      {
        title: "Güvenli PlentyONE Operasyonu",
        items: [
          "PlentyONE'un iç sipariş kimliği geri-yazma için korunurken müşteriye gerçek pazar yeri sipariş numarası gösterilir.",
          "Geri-yazma mağaza anahtarı kapalıysa etiket oluşturma PlentyONE üzerinde herhangi bir değişiklik yapmaz.",
          "customer@kenpost.de mağazası üç günlük otomatik sipariş penceresi ve mağazaya özel geri-yazma ayarıyla hazırlandı.",
        ],
      },
    ],
  },
  {
    version: 482,
    date: "2026-09-12",
    title: "PlentyONE Mağaza Entegrasyonu Eklendi",
    summary:
      "PlentyONE mağazaları müşteri paneline eklendi; siparişler KenPost'a alınabilir ve oluşturulan kargo takip bilgisi güvenli bağlantı üzerinden PlentyONE'a geri yazılabilir.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "PlentyONE, müşteri panelindeki Mağazalar bölümünde orijinal logosu ve bağlantı formuyla yer alır.",
      "Satış siparişleri adres, sipariş durumu ve gönderim bilgileriyle birlikte otomatik veya manuel olarak alınır.",
      "PlentyONE'da gönderilmiş olan siparişler KenPost'ta gönderildi, iptal edilenler iptal olarak gösterilir.",
      "KenPost etiketi oluşturulduğunda takip paketi ve gönderim kaydı PlentyONE'a geri yazılmaya hazırdır.",
    ],
    details: [
      {
        title: "Güvenli PlentyONE Bağlantısı",
        items: [
          "Sipariş okuma ve kargo yazma işlemleri ayrı yollar üzerinden çalışır; bağlantı anahtarları tarayıcıya veya mağaza adresine eklenmez.",
          "Tekrarlanan takip aktarımında aynı paket numarası yeniden oluşturulmaz.",
          "customer@kenpost.de hesabındaki PlentyONE mağazası otomatik sipariş senkronuna bağlandı.",
        ],
      },
    ],
  },
  {
    version: 481,
    date: "2026-09-12",
    title: "Sipariş Tarih Filtresi Hatırlanıyor",
    summary:
      "Sipariş listesine tüm tarihler, son 7 gün, son 30 gün ve son 3 ay görünüm seçenekleri eklendi; müşterinin seçimi sonraki ziyaretlerde korunur.",
    confidence: "operational",
    audience: ["customer", "system"],
    highlights: [
      "Varsayılan sipariş görünümü Tüm tarihler olarak kalır.",
      "Tarih seçimi yalnız sipariş listesini ve CSV çıktısını süzer; mağaza senkronizasyon aralığını değiştirmez.",
      "Müşterinin son tercihi tarayıcıda saklanır ve sipariş ekranı yeniden açıldığında uygulanır.",
    ],
    details: [
      {
        title: "Hatırlanan Liste Filtresi",
        items: [
          "Müşteri son 7 gün, son 30 gün veya son 3 ay seçeneklerinden birini seçerek mevcut siparişler arasında tarih bazlı çalışabilir.",
          "Temizle işlemi görünümü yeniden Tüm tarihler seçeneğine getirir ve bu tercih yeni varsayılan olarak saklanır.",
          "Filtre aktif ve arşivlenmiş sipariş görünümlerinde aynı şekilde uygulanır.",
        ],
      },
    ],
  },
  {
    version: 480,
    date: "2026-09-11",
    title: "eBay Takip Aktarımı ve Sipariş Arşivi Düzeltildi",
    summary:
      "eBay'in eski ve güncel sipariş kimliklerinin farklı olduğu kayıtlarda takip aktarımı kendini onarır; arşiv görünümü de aktif liste filtrelerinden etkilenmeden etiketli siparişleri gösterir.",
    confidence: "operational",
    audience: ["customer", "system"],
    highlights: [
      "eBay bir sipariş kimliğini geçersiz bulursa KenPost güncel sipariş önbelleğini ve canlı eBay listesini kullanarak doğru API kimliğini bulup takip aktarımını yeniden dener.",
      "Normal eBay etiketlerinde çalışan mevcut takip yazma ve canlı doğrulama akışı korunur; alternatif kimlik araması yalnız 32100 Geçersiz Sipariş Kimliği hatasında devreye girer.",
      "Arşivlenmiş siparişler ekranı artık açık/etiket bekleyen varsayılan filtreleri taşımadığı için etiketi oluşturulmuş kayıtları gizlemez.",
      "Malabavo Marketing hesabındaki 16-15130-68826 siparişinin etiketi, arşiv kaydı ve eBay takip aktarımı canlı veriden kontrol edildi.",
    ],
    details: [
      {
        title: "eBay Kimlik Kurtarma",
        items: [
          "Siparişe ait saklanan kimlik eBay tarafından reddedildiğinde aynı siparişin güncel orderId, eski sipariş numarası ve diğer güvenli referansları birlikte değerlendirilir.",
          "Takip numarası ancak eBay'in resmi gönderi kaydında aynı değer okunarak doğrulandıktan sonra başarılı kabul edilir.",
        ],
      },
      {
        title: "Arşiv Görünürlüğü",
        items: [
          "Arşiv görünümünde etiket bekleyen, açık durum ve yalnız gönderilmemiş sipariş filtreleri otomatik olarak devreden çıkar.",
          "Platform ve mağaza seçimi korunur; müşteri arşivde yalnız istediği mağazaya ait kayıtları süzmeye devam edebilir.",
        ],
      },
    ],
  },
  {
    version: 479,
    date: "2026-09-10",
    title: "Panel Menü ve Sayfa Geçişleri Hızlandırıldı",
    summary:
      "Menü geçişlerinde tüm ekranı tekrar tekrar tarayan gereksiz çeviri işlemi kaldırıldı; büyük listeler yüklenirken oluşan değişiklikler artık yalnız değişen bölümde işleniyor.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Türkçe panelde her sayfa ve tablo değişikliğinde çalışan gereksiz tam ekran taraması tamamen devre dışı bırakıldı.",
      "İngilizce ve Almanca panellerde yeni içerik geldiğinde bütün sayfa yerine yalnız eklenen veya değişen alan çevrilir.",
      "Büyük sipariş, gönderi ve rapor listelerinde menü tıklamasıyla başlayan ana iş parçacığı yükü azaltıldı.",
      "Dil değiştirildiğinde kalıcı menü ve üst çubuk metinlerinin doğru dile dönmesi korunur.",
    ],
    details: [
      {
        title: "Sayfa Geçiş Performansı",
        items: [
          "Önceki izleyici, tek bir satır veya yükleniyor göstergesi değiştiğinde bile paneldeki bütün düğme, tablo ve metinleri yeniden dolaşıyordu.",
          "Yeni izleyici yalnız değişen metni, özelliği veya eklenen alt ağacı işler; değişiklik maliyeti ekranın toplam büyüklüğünden bağımsız hale gelir.",
          "Varsayılan Türkçe arayüz kaynak metinleri zaten Türkçe olduğu için arka planda çeviri izleyicisi kurulmaz.",
        ],
      },
    ],
  },
  {
    version: 478,
    date: "2026-09-10",
    title: "Almanca Gönderi İptal Onayı Düzeltildi",
    summary:
      "Almanca müşteri panelindeki iptal penceresinde işlemi kapatan düğmenin yanlışlıkla gönderi iptali gibi görünmesine neden olan çeviri düzeltildi.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Almanca onay penceresindeki vazgeçme düğmesi artık doğru biçimde Abbrechen olarak gösterilir.",
      "Gönderiyi gerçekten iptal eden Sendung stornieren düğmesi, işlemi kapatan düğmeden açıkça ayrılır.",
      "Düzeltme müşteri hesabındaki manuel ve siparişe bağlı gönderi iptal ekranlarında geçerlidir.",
      "DHL hareketi olan gönderilerin iptal edilmesini engelleyen mevcut güvenlik kontrolü korunur.",
    ],
    details: [
      {
        title: "Almanca İptal Akışı",
        items: [
          "Önceki Almanca metinde Vazgeç düğmesi Stornieren olarak çevrildiği için müşteri iptali onayladığını sanarak pencereyi kapatabiliyordu.",
          "İşlemi kapatan düğme Abbrechen, gerçek iptal düğmesi Sendung stornieren olarak gösterilir.",
          "Lehrer:innen in DE. hesabındaki mevcut dört gönderi değiştirilmedi; müşteri güncel ekrandan uygun gönderileri kendisi iptal edebilir.",
        ],
      },
    ],
  },
  {
    version: 477,
    date: "2026-09-10",
    title: "Etiket Alım Raporu ve Çift Shopify Kredisi Düzeltildi",
    summary:
      "Tamamlanmamış veya iptal edilmiş etiket alımları yönetici raporlarından çıkarıldı; aynı ödenmiş Shopify siparişinin eşzamanlı kontrollerde iki kez krediye dönüşmesi engellendi.",
    confidence: "operational",
    audience: ["admin", "system"],
    highlights: [
      "Etiket alım ve atama raporu artık yalnız tamamlanmış işlemleri gösterir ve toplamlara yalnız bu işlemleri katar.",
      "Aynı Shopify siparişine ait aynı ürün satırları raporda tekrar etmez.",
      "Shopify ödeme kredilerine sabit işlem kimliği eklendi; müşteri, zamanlanmış görev ve webhook aynı anda çalışsa bile tek kredi oluşturulur.",
      "DK Selection hesabında iki kez işlenen aynı 100 etiketlik siparişin kullanılmamış ikinci kaydı düzeltilip etiketler havuza iade edildi.",
    ],
    details: [
      {
        title: "Rapor Doğruluğu",
        items: [
          "İptal edilmiş etiket alımları hem hareket listesinden hem Shopify alım/fatura özetinden hariç tutulur.",
          "Filtreli adetler ve hareket sayısı, ekranda uygulanan Shopify tekrar temizliği sonrasında yeniden hesaplanır.",
          "Tek yönetici işleminde iki farklı ürün verilmişse ürün satırları korunur; gerçek tekrarlar sipariş ve ürün kimliğine göre birleştirilir.",
        ],
      },
      {
        title: "Çift Kredi Koruması",
        items: [
          "Her ödenmiş Shopify siparişi, müşteri ve etiket ürünü için aynı sabit alım kimliğini kullanır.",
          "İki denetim aynı anda başlarsa veritabanı ikinci alımı atomik olarak reddeder ve ayrılan geçici stok otomatik serbest bırakılır.",
          "İkinci denetim ilk işlemin tamamlandığını görürse hata yerine zaten işlendi sonucunu döndürür.",
        ],
      },
    ],
  },
  {
    version: 476,
    date: "2026-09-10",
    title: "Tuba Store Etiket PDF ve Manuel İptal Sorunları Düzeltildi",
    summary:
      "Eski havuzdan yeniden etkinleştirilen DHL etiketlerinin boş görünmesine neden olan PDF form bağlantısı kaybı ve manuel etiket sayfasında iptal onayının açılmaması giderildi.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Yeniden etkinleştirilen PDF'lerde sayfada kalan form alanları belge form ağacına yeniden bağlanır.",
      "Normal PDF'ye dönüştürme öncesi eksik form bağlantıları otomatik onarılır; gönderen, alıcı, takip numarası ve barkodlar çıktıda korunur.",
      "Tuba Store'un 10 Eylül'de boş oluşan üç etiketi yeniden oluşturulup canlı depolamadaki Adobe ve normal PDF dosyaları düzeltildi.",
      "Manuel etiket listesindeki İptal düğmesi artık onay penceresini açar ve güvenli DHL kontrolünden sonra etiketi krediye geri verebilir.",
    ],
    details: [
      {
        title: "Eski Kaynaklarla Uyum",
        items: [
          "Süresi dolduğu için yeni güvenli revizyona çevrilen eski DHL PDF'lerinde widget alanları korunurken kaybolan AcroForm kaydı yeniden kurulur.",
          "Onarım hem güvenli yeniden etkinleştirme sırasında hem etiket oluşturma ve düz PDF'ye çevirme sırasında uygulanır.",
          "Normal kaynaklardan üretilen mevcut etiket davranışı değişmez; sorunlu eski kaynaklar artık aynı çıktı yolunu güvenle kullanır.",
        ],
      },
      {
        title: "Manuel Etiket İptali",
        items: [
          "Manuel etiket oluşturma sayfasının ayrı görünümünde eksik kalan ortak onay penceresi eklendi.",
          "İptal düğmesine basıldığında işlem artık görünmez biçimde beklemez; kullanıcı onayından sonra mevcut güvenli taşıyıcı kontrolü çalışır.",
          "DHL'de hareketi olmayan etiket iptal edilirse kredi müşteriye geri döner; hareket bulunan etiketlerde mevcut koruma devam eder.",
        ],
      },
    ],
  },
  {
    version: 475,
    date: "2026-09-10",
    title: "Müşteri Hesabından Admin Panele Güvenli Dönüş Eklendi",
    summary:
      "Admin destek modunda müşteri hesabı incelenirken normal çıkış işlemi yerine ana yönetici oturumuna doğrudan dönüş gösterilir.",
    confidence: "operational",
    audience: ["admin", "system"],
    highlights: [
      "Müşteri panelinin sağ üstündeki Çıkış Yap düğmesi, destek modunda Admin panele dön düğmesine dönüşür.",
      "Dönüş düğmesi müşteri oturumunu kapatmak yerine korunan yönetici oturumunu geri yükler.",
      "Destek modu dışında normal müşteri ve admin çıkış davranışı değiştirilmez.",
    ],
    details: [
      {
        title: "Kesintisiz Destek Geçişi",
        items: [
          "Admin panelindeki Hesaba geç veya Müşteri olarak gör işlemiyle açılan müşteri panelinde dönüş seçeneği üst çubukta sürekli görünür.",
          "Dönüş işlemi müşterinin detay sayfasına yönlendirir; yeniden e-posta ve şifre girilmesi gerekmez.",
          "Yanlışlıkla tüm oturumu kapatma riskini azaltmak için dönüş düğmesi farklı renk ve geri ok simgesiyle ayırt edilir.",
        ],
      },
    ],
  },
  {
    version: 474,
    date: "2026-09-09",
    title: "Kayıtta Referans Kodu İsteğe Bağlı Hale Getirildi",
    summary:
      "Yeni müşteri kaydında referans kodu olmadan devam edilebilir; alanın isteğe bağlı olduğu Türkçe, İngilizce ve Almanca kayıt ekranlarında açıkça gösterilir.",
    confidence: "operational",
    audience: ["customer", "system"],
    highlights: [
      "Boş referans kodu kayıt isteğine gönderilmez ve müşteri hesabı normal biçimde oluşturulur.",
      "Referans bağlantısıyla gelen geçerli kodlar eskisi gibi korunur ve affiliate ilişkisine bağlanır.",
      "Kayıt alanı Almanca ekranda Empfehlungscode (optional) olarak açıklanır.",
    ],
    details: [
      {
        title: "İsteğe Bağlı Kayıt Alanı",
        items: [
          "Referans kodu alanında tarayıcı zorunluluğu yoktur; boş değer sunucuya gönderilmez.",
          "Kayıt için e-posta, telefon ve şifre zorunlu olmaya devam eder; firma ve referans kodu isteğe bağlıdır.",
          "Türkçe, İngilizce ve Almanca etiketler alanın isteğe bağlı olduğunu doğrudan belirtir.",
        ],
      },
    ],
  },
  {
    version: 473,
    date: "2026-09-09",
    title: "Tüm Gönderileri Seçme ve Tek PDF İndirme Geri Getirildi",
    summary:
      "Gönderiler ekranında yalnız mevcut sayfa yerine filtrelenmiş sonuçların tamamı seçilebilir ve seçilen etiketler yazdırmaya hazır tek bir PDF dosyası olarak indirilebilir.",
    confidence: "operational",
    audience: ["customer", "system"],
    highlights: [
      "Tüm filtrelenen gönderileri seç işlemi, 50 satırlık sayfa sınırından bağımsız çalışır; 92 kayıt gibi birden fazla sayfaya yayılan sonuçları tek seçimde toplar.",
      "Seçili tek PDF indir işlemi, ZIP içindeki ayrı dosyalar yerine etiketleri tek yazdırılabilir PDF belgesinde birleştirir.",
      "Toplu PDF işlemi ilerleme sayısını gösterir ve mevcut 100 etiketlik güvenli işlem sınırını açıkça uygular.",
      "Yeni seçim ve indirme metinleri Almanca ve İngilizce müşteri panellerine eklendi.",
    ],
    details: [
      {
        title: "Gönderi Seçimi",
        items: [
          "Eski sayfa seçimi korunurken filtrelenmiş sonuçların tamamını seçen ayrı bir işlem eklendi.",
          "Seçim yalnız aktif ve dosyası hazır gönderileri kapsar; iptal edilmiş, teknik hata almış veya PDF'si henüz hazır olmayan kayıtlar işleme alınmaz.",
          "Sonuç sayısı 100'ü aşarsa tarayıcı belleğini ve indirme bütünlüğünü korumak için ilk 100 indirilebilir gönderi seçilir ve sınır kullanıcıya bildirilir.",
        ],
      },
      {
        title: "Tek Dosyada Yazdırma",
        items: [
          "Seçilen gönderilerin PDF sayfaları tarayıcıda tek belgede birleştirilir; eksik dosya varsa yarım belge indirilmez.",
          "Tamamlanan birleşik indirme toplu arşiv geçmişine kaydedilir; mevcut ZIP indirme seçeneği ayrıca kullanılmaya devam eder.",
        ],
      },
    ],
  },
  {
    version: 472,
    date: "2026-09-09",
    title: "Çok Kolili Sipariş ve Yeni Takip Numaralı Etiket Eklendi",
    summary:
      "Tek sipariş için birden fazla koli etiketi ayrı takip numaralarıyla oluşturulabilir; mevcut etiket PDF onarımı ile yeni takip numaralı güvenli etiket değişimi birbirinden ayrıldı.",
    confidence: "operational",
    audience: ["customer", "admin", "system"],
    highlights: [
      "Sipariş etiketi penceresine koli sayısı eklendi; her koli ayrı kredi, etiket ve takip numarası kullanır.",
      "Çok kolili etiketler tek bir PDF dosyasında indirilir ve WooCommerce siparişine tüm aktif takip numaraları birlikte yazılır.",
      "Gönderi menüsündeki aynı takip numaralı dosya onarımı artık PDF’yi yeniden oluştur adıyla açıkça gösterilir.",
      "Yeni takip numarasıyla değiştir işlemi, DHL hareketini canlı doğrular; eski etiketi güvenle iptal eder ve farklı kaynaktan yeni takipli etiket üretir.",
    ],
    details: [
      {
        title: "Bir Sipariş, Birden Fazla Koli",
        items: [
          "Müşteri 1 ile 50 arasında koli sayısı seçebilir; işlem başlamadan önce seçilen üründe tüm kolilere yetecek kredi denetlenir.",
          "İlk koli normal sipariş etiketi olarak oluşturulur; sonraki koliler aynı siparişe bağlı ek gönderiler olarak kaydedilir ve birbirinden farklı takip numarası alır.",
          "Kısmi bir hata oluşursa tamamlanan etiketler kaybolmaz, sonuç müşteriye açıkça bildirilir ve başarılı etiketler indirilebilir.",
        ],
      },
      {
        title: "Etiket Onarımı ve Takip Değişimi",
        items: [
          "PDF’yi yeniden oluştur işlemi mevcut takip numarasını korur ve yalnız dosyaları tekrar hazırlar.",
          "Yeni takip numarasıyla değiştir işleminde canlı DHL kontrolü tamamlanmadan iptal veya kredi hareketi yapılmaz; taşıyıcı hareketi bulunan gönderi değiştirilmez.",
          "Eski kaynak aynı işlemde yeniden seçilmez. WooCommerce tarafında iptal edilen takip kaydı kaldırılır, varsa diğer aktif koli takipleri korunur ve yeni takip eklenir.",
        ],
      },
    ],
  },
  {
    version: 471,
    date: "2026-09-08",
    title: "Etiket PDF Adı ve İlk Oluşturma Yılı Korundu",
    summary:
      "Yeniden yayınlanan kaynak etiketler indirilirken yüklenen orijinal DHL dosya adını korur; envanterde PDF'nin ilk oluşturma tarihi ve sisteme yüklenme tarihi yıl bilgisiyle gösterilir.",
    confidence: "operational",
    audience: ["admin", "system"],
    highlights: [
      "İndirilen tekil kaynak PDF, depolama içindeki teknik dosya adı yerine orijinal DHL Paket, ağırlık ve takip numarası içeren adı kullanır.",
      "Toplu ZIP indirmelerinde de her PDF'nin orijinal DHL dosya adı korunur.",
      "PDF içinden okunan ilk oluşturma tarihi artık gün, ay, yıl ve saat bilgisiyle eksiksiz görünür.",
      "KenPost'a yükleme tarihi de yıl bilgisiyle gösterilerek eski PDF tarihi ile sisteme alınma tarihi açıkça ayrılır.",
    ],
    details: [
      {
        title: "Orijinal Dosya Adı",
        items: [
          "Normal yükleme ve süresi geçen etiketi yeniden yayınlama akışlarında saklanan orijinal dosya adı indirme yanıtına taşınır.",
          "Toplu arşiv hazırlanırken PDF'lere sıra numaralı teknik ad verilmez; yüklenen orijinal ad kullanılır.",
          "Tarayıcı uyumluluğu için güvenli ASCII adla birlikte UTF-8 dosya adı da gönderilir; teknik depolama yolu kullanıcıya dosya adı olarak gösterilmez.",
        ],
      },
      {
        title: "Tarih Ayrımı",
        items: [
          "PDF oluşturma sütunu belgenin içinden okunan ilk tarihi yıl dahil gösterir.",
          "Yükleme sütunu PDF'nin KenPost sistemine alındığı zamanı yıl dahil gösterir; iki tarih birbirine karışmaz.",
        ],
      },
    ],
  },
  {
    version: 470,
    date: "2026-09-08",
    title: "Kartlı Etiket Alımı İçin Hata Takibi Güçlendirildi",
    summary:
      "Müşterinin kartlı etiket ödeme bağlantısı oluşturma denemeleri artık müşteri ve işlem bilgisiyle izlenir; başarısız denemeler anlaşılır mesaj ve destek referansıyla kaydedilir.",
    confidence: "operational",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Başarıyla oluşturulan Shopify ödeme bağlantıları müşteri, adet, toplam ve taslak sipariş kimliğiyle denetim geçmişine yazılır.",
      "Shopify, stok, ürün veya ödeme oturumu hataları müşteriye teknik ayrıntı göstermeden anlaşılır Türkçe mesajla açıklanır.",
      "Başarısız her denemeye destek referansı verilir; yönetim panelindeki hata kayıtlarından ilgili müşteriyle birlikte bulunabilir.",
    ],
    details: [
      {
        title: "Faktum Trade Kontrolü",
        items: [
          "Erhan Cimen / Faktum Trade hesabının aktif olduğu, müşteri fiyatlarının tanımlı olduğu ve tüm ürünlerde yeterli canlı stok bulunduğu doğrulandı.",
          "Müşterinin satın alma ekranını açtığı fakat yeni bir ödemeyi tamamlamadığı görüldü; aynı kart altyapısında bugün başka müşterilerin başarılı ödemeleri bulunduğu doğrulandı.",
        ],
      },
      {
        title: "Kalıcı Teşhis Kaydı",
        items: [
          "Ödeme bağlantısı oluşturma, ödeme doğrulama ve yakın dönem mutabakat hataları ayrı alanlarda kaydedilir.",
          "Gizli ödeme belirteçleri ve bağlantılar hata kaydına yazılmaz; yalnız güvenli işlem özeti ile teknik hata saklanır.",
        ],
      },
    ],
  },
  {
    version: 469,
    date: "2026-09-08",
    title: "Karantina Etiketlerinde Toplu Yeniden Yayınlama Düzeltildi",
    summary:
      "Süresi dolmuş PDF etiketleri güvenli küçük paketlerle yeniden hazırlanır; yarıda kalan eski işlemlerin bıraktığı seçim kilitleri otomatik kaldırılır ve 90 etiketlik admin seçimi kesintisiz ilerler.",
    confidence: "operational",
    audience: ["admin", "system"],
    highlights: [
      "Yarım kalan üç toplu işlemden kalan 260 eski admin kilidi tespit edilip güvenle temizlendi.",
      "Admin tek seferde 90 etikete kadar seçmeye devam eder; süresi geçmiş PDF'ler arka planda güvenli 10'lu paketler halinde yeniden yayınlanır.",
      "Beş dakikadan eski yarım kalmış admin yeniden-yayınlama kilitleri liste açılırken ve yeni işlem başlarken otomatik toparlanır.",
    ],
    details: [
      {
        title: "Dayanıklı Toplu İşlem",
        items: [
          "PDF üzerindeki eski süre kuralı kaldırılmış güvenli kopya üretimi küçük işlem paketlerine bölündü; uzun tek isteğin zaman aşımında tüm seçimi kilitlemesi önlendi.",
          "Bir paket hata verirse daha önce başarıyla tamamlanan paketler açıkça bildirilir ve liste güncel durumla yeniden yüklenir.",
        ],
      },
      {
        title: "Otomatik Kilit Kurtarma",
        items: [
          "Yalnız admin yeniden-yayınlama işaretli ve süresi geçmiş işlem kilitleri kaldırılır; sevkiyat veya diğer sistem rezervasyonlarına dokunulmaz.",
          "Sunucu, doğrudan yapılan aşırı büyük süresi geçmiş PDF isteklerini kayıtları kilitlemeden önce reddeder.",
        ],
      },
    ],
  },
  {
    version: 468,
    date: "2026-09-07",
    title: "Manuel Etiket ve Affiliate Bonusu Kaynakları Düzeltildi",
    summary:
      "Admin tarafından tanımlanan etiketler ödeme zamanından bağımsız olarak Manuel admin kategorisinde tutulur; yalnız affiliate kazancından etikete çevrilen hareketler Bonus sayılır ve affiliate müşterilerin aylık alım/kullanımı görünür.",
    confidence: "operational",
    audience: ["admin", "customer", "system"],
    highlights: [
      "PC-Laptop-Center dahil admin panelinden tanımlanan etiketler, sıfır tutarlı veya sonradan ödenecek olsa da yanlışlıkla Bonus kategorisine girmez.",
      "Bonus kategorisi yalnız affiliate kazancından etikete çevrilen etiketleri kapsar; müşteri satın alımları kendi kategorisinde kalır.",
      "Yönlendirilen müşteriler tablosu artık bu ay satın alınan etiket adedini/tutarını ve bu ay kullanılan etiket adedini tüm zamanlar toplamıyla birlikte gösterir.",
    ],
    details: [
      {
        title: "Kaynak Sınıflandırması",
        items: [
          "Adminin doğrudan yaptığı tüm etiket tanımlamaları Manuel admin kaynağına alınır; fiyatın sıfır olması tek başına bonus kabul edilmez.",
          "Affiliate kazanç dönüşüm kimliği bulunan etiketler bağımsız Affiliate bonusu kaynağında gösterilir.",
        ],
      },
      {
        title: "Aylık Affiliate Müşteri Hareketi",
        items: [
          "Her yönlendirilen müşteri için ay başından bugüne tamamlanan satın alma adedi ve net tutarı hesaplanır.",
          "Başarılı giden gönderiler üzerinden aylık kullanılan etiket adedi hesaplanır; satır ayrıntısında tüm zamanlar karşılığı korunur.",
        ],
      },
    ],
  },
  {
    version: 467,
    date: "2026-09-07",
    title: "Bonus Etiketler Ayrı Kategori ve Aylık Kullanım Raporuna Alındı",
    summary:
      "Yönetim panelinde bonus, manuel admin ataması ve müşteri alımı birbirinden ayrıldı; gerçekten kullanılan bonusların aylık adedi ve net parasal karşılığı ayrıca görünür hale getirildi.",
    confidence: "implemented",
    audience: ["admin", "system"],
    highlights: [
      "Etiket hareketleri Bonus, Manuel admin ve Müşteri alımı olarak üç ayrı kategoride filtrelenebilir ve renklerle ayırt edilir.",
      "Bu ay gerçekten kullanılan bonus etiketlerin adedi, ortalama net birim fiyatı ve toplam net karşılığı yönetim panelinde ayrı kartlarda gösterilir.",
      "Bonus değeri, eski sıfır tutarlı bonus kayıtlarında dahi müşteriye tanımlanan etiket fiyatı üzerinden hesaplanır.",
    ],
    details: [
      {
        title: "Bonus Hareketleri",
        items: [
          "Bonus tanımlamaları artık manuel admin atamalarının içinde gösterilmez; özet, filtre ve hareket satırlarında bağımsız Bonus kategorisine alınır.",
          "Eski ve yeni bonus işaretleri birlikte tanınır; normal ücretli manuel atamalar ayrı kalır.",
        ],
      },
      {
        title: "Aylık Bonus Kullanımı",
        items: [
          "Aylık rapor yalnız müşterinin gerçekten tükettiği bonus etiketleri sayar; hesaba eklenmiş fakat henüz kullanılmamış bonuslar kullanıma dahil edilmez.",
          "Rapor bonus başına ortalama net fiyatı ve kullanılan bonusların toplam net karşılığını gösterir.",
        ],
      },
    ],
  },
  {
    version: 466,
    date: "2026-09-07",
    title: "TurkMarket WooCommerce Bağlantısı Canlıda Doğrulandı",
    summary:
      "TurkMarket, Anadolu Tadı müşteri hesabına mevcut mağazadan bağımsız bir WooCommerce bağlantısı olarak eklendi; okuma/yazma yetkileri ve Almanya ile sınırlı otomatik sipariş senkronu canlıda doğrulandı.",
    confidence: "operational",
    audience: ["customer", "system"],
    highlights: [
      "TurkMarket.eu bağlantısı Anadolu Tadı hesabında ayrı mağaza olarak etkinleştirildi; mevcut anadolutadi.com bağlantısı değiştirilmedi.",
      "WooCommerce API anahtarının okuma ve yazma yetkileri gerçek siparişleri değiştirmeden doğrulandı; otomatik senkron 30 dakikada bir çalışacak şekilde açıldı.",
      "İlk ülke filtreli senkron 106 kaydı taradı, Almanya adresli 66 kaydı aldı ve diğer ülkelere ait 40 kaydı müşteri ekranının dışında bıraktı.",
    ],
    details: [
      {
        title: "Bağlantı ve Yetki",
        items: [
          "İlk verilen anahtar yalnız yazma yetkisine sahip olduğu için bağlantı başarısız döngüye alınmadan bekletildi; okuma/yazma yetkisi verildikten sonra aynı mağaza güvenli biçimde etkinleştirildi.",
          "Sipariş okuma ve takip geri yazma yetkileri ayrı ayrı sınandı; yazma kontrolünde gerçek bir WooCommerce siparişi değiştirilmedi.",
        ],
      },
      {
        title: "Almanya Sipariş Kapsamı",
        items: [
          "Mağazaya özel ülke filtresi TurkMarket için Almanya olarak etkin tutuldu; diğer WooCommerce müşterilerinde varsayılan kapalı davranış korundu.",
          "106 sayısının aktif sipariş adedi değil ilk bağlantıda taranan son 30 günlük toplam kayıt olduğu netleştirildi; teslim, iptal ve başarısız durumlar bu toplamın içinde kalabilir.",
        ],
      },
    ],
  },
  {
    version: 465,
    date: "2026-09-07",
    title: "Etiket Hareketleri ve WooCommerce Ülke Filtresi",
    summary:
      "Yönetim paneli, manuel etiket atamalarını ve müşteri alımlarını dönem ve ürün bazında birlikte gösterir; WooCommerce mağazaları da isteğe bağlı ülke filtresiyle sipariş alabilir.",
    confidence: "implemented",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Etiket hareketleri bugün, bu hafta, bu ay ve tüm zamanlar seçenekleriyle; Kleinpaket, 2 kg, 5 kg ve diğer ürünlere göre filtrelenebilir.",
      "Manuel atamalar yeşil, müşteri alımları turuncu gösterilir; bonus etiketlerin normal bakiye gibi otomatik kullanıldığı müşteri ekranında açıklanır.",
      "WooCommerce ülke filtresi varsayılan olarak kapalıdır ve yalnız mağaza bazında etkinleştirilir.",
    ],
    details: [
      {
        title: "Stok ve Alım Görünürlüğü",
        items: [
          "Shopify fatura satırlarında gerçek etiket ürün kırılımı görünür hale getirildi; hizmet açıklaması artık Kleinpaket veya ağırlık ürününü gizlemiyor.",
          "Kleinpaket canlı stoğu ve son atama, kullanım, süre dolumu hareketleri doğrudan üretim veritabanından doğrulandı.",
        ],
      },
      {
        title: "Bonus ve WooCommerce Kullanımı",
        items: [
          "Bonus etiketler alım hareketlerinde açıkça işaretlenir ve müşterinin ilgili ürün bakiyesinden ayrı kod veya ödeme olmadan kullanılır.",
          "WooCommerce ülke seçimi mağazaya özel saklanır; filtre kapalı olan diğer müşterilerin sipariş akışı değişmez.",
        ],
      },
    ],
  },
  {
    version: 464,
    date: "2026-08-21",
    title: "Karantinadaki Etiketler İçin Kontrollü Admin Yetkisi",
    summary:
      "Admin, PDF içindeki doğrulanmış geçerlilik süresi dolmuş ve diğer güvenlik kontrollerinden geçen etiketleri açık risk onayıyla yeniden satış havuzuna alabilir.",
    confidence: "implemented",
    audience: ["admin", "system"],
    highlights: [
      "Süresi geçmiş PDF istisnası yalnız admin oturumunda, seçili etiketlerde ve iki ayrı onay adımından sonra çalışır.",
      "Müşteri, otomatik bakım ve standart yeniden yayınlama akışları süresi geçmiş PDF'leri açmaya devam edemez.",
    ],
    details: [
      {
        title: "Güvenlik ve Denetim",
        items: [
          "Gerçek kullanım, DHL hareketi, bekleyen taşıyıcı kontrolü, rezervasyon ve müşteri koruma süresi kuralları admin PDF istisnasında da uygulanır.",
          "Açık risk onayı denetim kaydına yazılır; PDF üzerindeki eski tarih değiştirilmez ve admin istisnası sonraki otomatik PDF kontrolünde korunur.",
          "Karantinadaki hiçbir etiket otomatik açılmaz; adminin etiketleri tek tek veya toplu seçmesi gerekir.",
        ],
      },
    ],
  },
  {
    version: 463,
    date: "2026-08-08",
    title: "Tanınan Cihazlarda Çoklu Hesapla Hızlı Giriş",
    summary:
      "Müşteriler daha önce güvenli biçimde giriş yaptıkları KenPost hesaplarını aynı tarayıcıda seçerek yeniden parola yazmadan açabilir; birden fazla hesap ayrı ayrı gösterilir ve cihazdan kaldırılabilir.",
    confidence: "implemented",
    audience: ["customer", "system"],
    highlights: [
      "Açık oturumu bulunan kullanıcılar doğrudan panele alınmaya devam eder; çıkış yapan kullanıcıya bu tarayıcıda tanınan hesaplar gösterilir.",
      "Parola değişikliği, parola sıfırlama, hesap pasifleştirme ve ekip üyesi yetki değişiklikleri tanınan cihaz erişimini otomatik olarak iptal eder.",
    ],
    details: [
      {
        title: "Güvenli Cihaz Tanıma",
        items: [
          "Tarayıcıda yalnızca HttpOnly ve SameSite korumalı rastgele cihaz anahtarı tutulur; veritabanında anahtarın yalnızca SHA-256 özeti saklanır ve IP adresi kimlik doğrulama amacıyla kullanılmaz.",
          "Admin hesapları parolasız cihaz girişine alınmaz; müşteri ve ekip hesapları 180 günlük kayan cihaz süresiyle normal 30 günlük KenPost oturumu oluşturur.",
        ],
      },
      {
        title: "Hesap Seçimi",
        items: [
          "Türkçe, Almanca ve İngilizce giriş ekranları; kayıtlı hesap seçimi, başka hesapla giriş ve hesabı yalnızca mevcut cihazdan kaldırma işlemlerini destekler.",
          "E-posta ile iki aşamalı doğrulama kullanan hesaplar ancak doğrulama başarıyla tamamlandıktan sonra cihaza eklenir.",
        ],
      },
    ],
  },
  {
    version: 462,
    date: "2026-08-06",
    title: "Shopware 6 Sipariş ve Takip Entegrasyonu Tamamlandı",
    summary:
      "Shopware 6 mağazaları Admin API üzerinden KenPost'a bağlanabilir, siparişler 30 dakikalık programa alınabilir ve oluşturulan takip numaraları doğru Shopware teslimatına doğrulanarak geri yazılabilir.",
    confidence: "implemented",
    audience: ["customer", "system"],
    highlights: [
      "Shopware siparişleri; müşteri, şirket, adres eki, ürün, SKU, ağırlık, durum ve satış kanalı bilgileri korunarak içe aktarılır.",
      "Takip numarası mevcut kodları silmeden doğru teslimata eklenir; birden fazla teslimat belirsizse işlem güvenli biçimde durdurulur.",
    ],
    details: [
      {
        title: "Bağlantı ve Güvenlik",
        items: [
          "Shopware 6 Admin API client_credentials bağlantısı, HTTPS zorunluluğu, zaman aşımı, kontrollü yeniden deneme, 401 token yenileme ve gizli anahtar maskeleme ile eklendi.",
          "Yeni mağaza kaydedilmeden önce API bağlantısı doğrulanır; Access Key ID ve Secret Access Key hiçbir müşteri API yanıtında düz metin olarak gösterilmez.",
        ],
      },
      {
        title: "Sipariş ve Takip Akışı",
        items: [
          "Tarih aralıklı ve sayfalı sipariş senkronu, 30 dakikalık otomatik zamanlayıcı, manuel senkron, ürün önizlemesi ve ham sipariş arşivi tamamlandı.",
          "Takip geri yazma sonrasında Shopware teslimatı yeniden okunarak sonuç doğrulanır; isteğe bağlı olarak teslimat durumu shipped aşamasına geçirilir.",
          "Gerçek mağaza bağlantısı, müşteri URL ve entegrasyon anahtarları sağlandığında yapılacaktır; kodlama sahte API ve tam test paketiyle doğrulanmıştır.",
        ],
      },
    ],
  },
];

const recentReleaseNotes: ReleaseEntry[] = [
  ...releaseNotesBackfill20260731To20260923,
  ...recentReleaseNoteSources
    .slice()
    .reverse()
    .map(({ version: _historicalVersion, ...release }) => release),
]
  .sort((left, right) => left.date.localeCompare(right.date))
  .map((release, index) => ({ ...release, version: 462 + index }))
  .reverse();

const cloudflareReleaseNotes: ReleaseEntry[] = [
  {
    version: 201,
    date: "2026-05-31",
    title: "Reporting Performance Indexes Are Live",
    summary:
      "Live D1 indexes were added for report, shipment, invoice, customer and purchase lists, and heavy screens now rely on pagination and filters instead of loading broad datasets at once.",
    confidence: "verified",
    audience: ["admin", "system"],
    highlights: [
      "Admin reporting and export screens now have dedicated D1 indexes for the most common date, status, customer and platform filters.",
      "The slowest customer, shipment, invoice and label-oriented screens were tightened around server-side pagination.",
    ],
    details: [
      {
        title: "Performance Coverage",
        items: [
          "The reporting index migration was applied to the live Cloudflare D1 database.",
          "The release was verified with the auth/carrier test suite, TypeScript checks and a production Vite build.",
        ],
      },
    ],
  },
  {
    version: 200,
    date: "2026-05-31",
    title: "Operations Report Center Uses Real Detail Pages",
    summary:
      "Admin report cards now open real detail views for sales, customer health, stock, carrier status, problem shipments, invoices and VAT, with CSV export paths where operational teams need them.",
    confidence: "verified",
    audience: ["admin"],
    highlights: [
      "Report catalog entries now drill into real `/admin/reports/:slug` detail screens.",
      "Operational exports were added for report workflows that need offline inspection or customer support follow-up.",
    ],
    details: [
      {
        title: "Admin Reporting",
        items: [
          "The report API now returns concrete datasets instead of placeholder report descriptions.",
          "The admin report overview keeps the control-report link for automatic marketplace refresh failures.",
        ],
      },
    ],
  },
  {
    version: 199,
    date: "2026-05-31",
    title: "Admin and Customer List Screens Were Hardened",
    summary:
      "Order, shipment, customer, invoice and purchase screens gained stronger server-side pagination, search, status filtering and export handling so large accounts stay usable.",
    confidence: "verified",
    audience: ["admin", "customer"],
    highlights: [
      "Customer-facing order and shipment lists now use the same scalable loading pattern as admin screens.",
      "Admin customer and billing screens expose clearer filters and CSV export entry points.",
    ],
    details: [
      {
        title: "List Reliability",
        items: [
          "Large customer and label datasets are no longer treated as one unbounded browser payload.",
          "The UI now makes active filters and exportable views easier to read during support work.",
        ],
      },
    ],
  },
  {
    version: 198,
    date: "2026-05-31",
    title: "Shipment and Carrier Statuses Are Clearer",
    summary:
      "DHL and other carrier signals are normalized into customer-readable shipment states such as delivered, in transit, return, problem and carrier check pending.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Carrier check errors are surfaced ahead of generic shipment states when they need operational attention.",
      "Return and branch states are mapped into understandable labels instead of raw carrier wording.",
    ],
    details: [
      {
        title: "Carrier Status Model",
        items: [
          "A shared carrier-status helper now drives the operational status shown in shipment and report screens.",
          "Dedicated carrier status tests cover delivered, error, return and branch-style states.",
        ],
      },
    ],
  },
  {
    version: 197,
    date: "2026-05-31",
    title: "S3 and R2 Label Archive Paths Were Rechecked",
    summary:
      "Label files, order JSON archives, signed download links and S3-compatible read, write, copy and delete helpers were reviewed against the Cloudflare version of the app.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "The S3-compatible helper layer continues to guard object keys and signed download URLs.",
      "Order JSON archives keep both plain and gzip upload paths for faster retrieval and smaller storage payloads.",
    ],
    details: [
      {
        title: "Storage Flow",
        items: [
          "The Cloudflare functions keep the old S3 scenario shape for labels, source files and order JSON archives.",
          "Public download routes continue to rely on signed tokens rather than exposing raw storage credentials.",
        ],
      },
    ],
  },
  {
    version: 196,
    date: "2026-05-31",
    title: "Shipping Address Takes Priority Over Billing Address",
    summary:
      "Platform orders, including JTL orders, now keep shipping and billing address snapshots separate. The shipping address is used for labels, while a different billing address remains visible for support and invoice context.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Orders now store the selected address source together with shipping and billing snapshots.",
      "Different billing addresses are shown in order, shipment and export surfaces without replacing the shipping destination.",
    ],
    details: [
      {
        title: "Address Safety",
        items: [
          "The address-source migration gives every order a shipping-first default and a billing fallback only when shipping data is unavailable.",
          "Customer and admin order details now expose when the billing address differs from the shipping address.",
        ],
      },
    ],
  },
  {
    version: 195,
    date: "2026-05-31",
    title: "Automatic Marketplace Order Refresh Was Added",
    summary:
      "Stores now have automatic order refresh enabled by default every 30 minutes, with store-level frequency settings from 5 minutes through daily and an admin control report for failed sync attempts.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Each store can control whether automatic order sync is enabled and how often it should run.",
      "Admin users can inspect failed marketplace refresh attempts in the control report before customers need to report them.",
    ],
    details: [
      {
        title: "Scheduled Sync",
        items: [
          "The default interval is 30 minutes for existing and new stores that support scheduled order sync.",
          "Sync runs record status, duration, fetched/imported counts and error messages for operational review.",
        ],
      },
    ],
  },
  {
    version: 194,
    date: "2026-05-31",
    title: "Language and Version Visibility Were Reviewed",
    summary:
      "Visible admin and customer panel text was reviewed again, and the product version entry point was aligned with the existing version-history model instead of a separate Cloudflare-only list.",
    confidence: "verified",
    audience: ["admin", "customer"],
    highlights: [
      "The Cloudflare app now follows the existing product version pattern that starts from the latest numeric release.",
      "The version area stays visible for customers and admins as a product-history surface.",
    ],
    details: [
      {
        title: "Version Tracking",
        items: [
          "The release note structure now uses numeric versions, confidence labels, audience tags, highlights and detail sections.",
          "The public `/versions` route and admin settings version area share the same release source.",
        ],
      },
    ],
  },
  {
    version: 193,
    date: "2026-05-31",
    title: "Sign In Can Remember the User Email",
    summary:
      "The sign-in screen now includes a remember-me option for the email address so returning users can sign in faster without storing their password in the browser.",
    confidence: "verified",
    audience: ["customer", "admin"],
    highlights: [
      "Only the email address is remembered when the user chooses the option.",
      "The password remains a normal current-password field and is not persisted by the app.",
    ],
    details: [
      {
        title: "Login UX",
        items: [
          "The remembered email is stored locally in the browser and can be cleared by turning the option off.",
          "The change is intentionally limited to convenience and does not alter account security rules.",
        ],
      },
    ],
  },
  {
    version: 192,
    date: "2026-05-31",
    title: "Legacy Password Migration Was Secured",
    summary:
      "Legacy bcrypt password hashes can now be verified during Cloudflare sign-in and rehashed into the new format, while accounts without a valid stored password no longer accept arbitrary first-login passwords.",
    confidence: "verified",
    audience: ["admin", "customer", "system"],
    highlights: [
      "Imported Gadget bcrypt hashes are verified before being converted to the new Cloudflare password format.",
      "Wrong passwords and missing legacy hashes do not claim or initialize an account password.",
    ],
    details: [
      {
        title: "Authentication Safety",
        items: [
          "The login flow now has tests for missing hashes, valid legacy bcrypt hashes, wrong legacy passwords and bcrypt rehashing.",
          "The legacy password table is indexed by email, user id and legacy user id for controlled migration lookups.",
        ],
      },
    ],
  },
];

export type { ReleaseAudience, ReleaseConfidence, ReleaseEntry };

export const releaseNotes: ReleaseEntry[] = [...recentReleaseNotes, ...releaseNotesV202ToV461, ...cloudflareReleaseNotes, ...legacyReleaseNotes];

export const latestRelease = releaseNotes[0];

export function getCurrentProductVersionLabel(): string {
  return `v${latestRelease.version}`;
}
